import { Fragment, useMemo } from 'react';
import { Empty, Tooltip } from 'antd';
import {
  AlertOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FileAddOutlined,
  InboxOutlined,
  MailOutlined,
  PartitionOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { TimelineEvent, TimelineRefs } from '@/api/types';
import type { Intent } from '@/workbench/meta';
import { INTENT_VAR } from '@/workbench/meta';
import { formatAbsolute, formatDay, formatRelative, toMs } from '@/lib/time';
import { erasedOr } from '@/lib/erased';

interface KindMeta {
  label: string;
  intent: Intent;
  icon: React.ReactNode;
}

const KIND_META: Record<string, KindMeta> = {
  GRAPH_CHANGE: { label: 'Graph change', intent: 'info', icon: <PartitionOutlined /> },
  EVIDENCE_ADDED: { label: 'Evidence added', intent: 'positive', icon: <FileAddOutlined /> },
  RUN_STARTED: { label: 'Run started', intent: 'processing', icon: <PlayCircleOutlined /> },
  RUN_COMPLETED: { label: 'Run completed', intent: 'positive', icon: <CheckCircleOutlined /> },
  DECISION_RECORDED: { label: 'Decision', intent: 'attention', icon: <AuditOutlined /> },
  SIGNAL_RECEIVED: { label: 'Signal', intent: 'negative', icon: <AlertOutlined /> },
  SIGNAL_RESOLVED: { label: 'Signal resolved', intent: 'positive', icon: <CheckCircleOutlined /> },
  CLOCK_EVENT: { label: 'Clock', intent: 'neutral', icon: <ClockCircleOutlined /> },
  WORK_ITEM: { label: 'Work item', intent: 'info', icon: <InboxOutlined /> },
  RFI: { label: 'RFI', intent: 'processing', icon: <MailOutlined /> },
  DOCUMENT_OPENED: { label: 'Document opened', intent: 'neutral', icon: <EyeOutlined /> },
};

function kindMeta(kind: string): KindMeta {
  return KIND_META[kind] ?? { label: kind, intent: 'neutral', icon: <PartitionOutlined /> };
}

/** The first linkable ref on an event (drives the click-through target), if any. */
function linkTarget(refs?: TimelineRefs): { kind: 'run' | 'workItem' | 'rfi'; id: string } | null {
  if (!refs) return null;
  if (refs.workItemId) return { kind: 'workItem', id: refs.workItemId };
  if (refs.rfiId) return { kind: 'rfi', id: refs.rfiId };
  if (refs.runId) return { kind: 'run', id: refs.runId };
  return null;
}

interface Props {
  events: TimelineEvent[];
  now?: number;
  /** Click a linkable event to focus its run / work-item / RFI where a surface exists. */
  onFocus?: (target: { kind: 'run' | 'workItem' | 'rfi'; id: string }, event: TimelineEvent) => void;
}

/** Newest-first entity timeline (DOM rail). Kind glyphs + colours; linkable events click through. */
export function EntityTimeline({ events, now, onFocus }: Props) {
  const grouped = useMemo(() => {
    const sorted = [...events].sort((a, b) => (toMs(b.at) ?? 0) - (toMs(a.at) ?? 0));
    const out: { day: string; items: TimelineEvent[] }[] = [];
    for (const ev of sorted) {
      const day = formatDay(ev.at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(ev);
      else out.push({ day, items: [ev] });
    }
    return out;
  }, [events]);

  if (events.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No events in this window." />;
  }

  return (
    <div className="ol-timeline">
      <div className="ol-timeline__rail" aria-hidden />
      {grouped.map((g) => (
        <Fragment key={g.day}>
          <div className="ol-timeline__day">{g.day}</div>
          {g.items.map((ev, i) => {
            const meta = kindMeta(String(ev.kind));
            const target = linkTarget(ev.refs);
            const clickable = Boolean(target && onFocus);
            const color = INTENT_VAR[meta.intent];
            return (
              <button
                key={`${ev.at}-${i}`}
                type="button"
                className={`ol-timeline__item${clickable ? ' ol-timeline__item--link' : ''}`}
                onClick={clickable && target ? () => onFocus!(target, ev) : undefined}
                disabled={!clickable}
                aria-label={`${meta.label}: ${erasedOr(ev.summary)}`}
              >
                <span className="ol-timeline__dot" style={{ borderColor: color, color }} aria-hidden>
                  {meta.icon}
                </span>
                <span className="ol-timeline__summary">{erasedOr(ev.summary)}</span>
                <span className="ol-timeline__meta">
                  <span style={{ color, fontWeight: 600 }}>{meta.label}</span>
                  <Tooltip title={formatAbsolute(ev.at)}>
                    <span>{formatRelative(ev.at, now)}</span>
                  </Tooltip>
                  {target && <span className="ol-mono">{target.kind}</span>}
                </span>
              </button>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
